import React, { useState } from 'react';
import urlCatalog from '@angi/url-catalog';
import { isUndefined } from 'lodash';
import classNames from 'classnames';
import { Row, Button } from 'angieslist-react-components';
import { Showcase } from '../../common/profileService';
import ShowcaseIndexCard from './ShowcaseIndexCard';
import ShowcasePublishConflictModal from '../../../showcase/browser/ShowcasePublishConflictModal';
import './ShowcaseGrid.scss';
import LockIcon from '../assets/lock.svg';
import CameraIcon from '../assets/Camera';
import { FREE_UPGRADE_STATE, PENDING_UPGRADE_STATE } from '../../../home/common/selectors/upgradeStateSelectors';


type Props = {
  publishedShowcases: Showcase[],
  serviceProviderId: string | number,
  archiveShowcase: (showcaseId: number, serviceProviderId: string | number) => void,
  showcasePendingRemoval?: boolean,
  switchTab: () => void,
  advertiseRequest: () => void,
  advertiseRequestPending: boolean,
  advertiseRequestSuccessful: boolean,
  sendEnhancedAnalyticsEvent: (rule: any) => void,
  serviceProviderUpgradeState: string,
};

const MAX_ACTIVE_SHOWCASES = 1;

const ShowcaseNonAdPublishedTab = ({
  publishedShowcases,
  serviceProviderId,
  archiveShowcase,
  showcasePendingRemoval,
  switchTab,
  advertiseRequest,
  advertiseRequestPending,
  advertiseRequestSuccessful,
  sendEnhancedAnalyticsEvent,
  serviceProviderUpgradeState,
}: Props) => {
  const activeShowcases = publishedShowcases.slice(0, MAX_ACTIVE_SHOWCASES);
  const inactiveShowcases = publishedShowcases.slice(MAX_ACTIVE_SHOWCASES);
  const showLimitCTA = activeShowcases.length === MAX_ACTIVE_SHOWCASES;

  const [showcaseToDisplay, setShowcaseToDisplay] = useState(undefined);

  const SHOWCASE_TOP_PX = 460; // approximate top of the showcase content

  const submitAdRequest = () => {
    window.scrollTo(0, SHOWCASE_TOP_PX);
    advertiseRequest('SP : My Business : Showcase Published', 'Learn More About Advertising Clicked');
  };

  const openDisplayModal = (showcaseId: number) => setShowcaseToDisplay(showcaseId);
  const hideDisplayModal = () => setShowcaseToDisplay(undefined);
  const freePros = serviceProviderUpgradeState === FREE_UPGRADE_STATE;
  const pending = serviceProviderUpgradeState === PENDING_UPGRADE_STATE;

  return (
    <div>
      <Row>
        <If condition={showLimitCTA && freePros}>
          <Row>
            <div id="nonad-showcase-grid--limit-alert" >
              <div id="showcase-limit-title-line">
                <div className="center-vertically">
                  <img src={LockIcon} alt="Lock Icon" />
                </div>
                <p id="showcase-limit-title">
                  Project showcase limit reached
                </p>
              </div>
              <p className="showcase-limit-text">
                You can only display one project showcase on your business profile right now.
                Upgrade to a paid Angi Ads account to publish unlimited showcases and choose which
                3 to display on your profile.
              </p>
              <If condition={!advertiseRequestSuccessful}>
                <div className="text-center showcase-limit-alert-button" >
                  <Button
                    bsStyle="primary-office"
                    bsSize="small"
                    className="pull-left"
                    id="nonad-showcase-grid--advertise-button"
                    onClick={submitAdRequest}
                    loading={advertiseRequestPending}
                    disabled={advertiseRequestPending}
                    flat
                  >
                    Become a Paid Advertiser
                  </Button>
                </div>
              </If>
            </div>
          </Row>
        </If>
      </Row>
      <Row>
        <div>
          <div
            id="add-a-project"
            className={!pending && 'show-pointer'}
          >
            <a
              id="add-a-project-link"
              className={pending && 'hide-pointer'}
              href={!pending && `/app/showcase/${serviceProviderId}`}
            >
              <span className="make-div-link" />
            </a>
            <div id="camera-icon">
              <CameraIcon
                className={classNames('icon', pending && 'icon-disabled')}
              />
            </div>
            <p
              id="add-a-project-text"
              className={pending ? 'disabled' : 'enabled'}
            >
              Add a project
            </p>
          </div>
          <If condition={!isUndefined(showcaseToDisplay)} >
            <ShowcasePublishConflictModal
              currentFirstOrderedPublishShowcase={activeShowcases[0]}
              refreshedPublishedShowcase={
                publishedShowcases.find(showcase => showcase.showcaseId === showcaseToDisplay)
              }
              allPublishedShowcases={publishedShowcases}
              showModal={!isUndefined(showcaseToDisplay)}
              serviceProviderId={serviceProviderId}
              sendEnhancedTrackEvent={sendEnhancedAnalyticsEvent}
              onHide={hideDisplayModal}
              headerText="Change Active Project"
              analyticsLocation="SP : My Business : Showcase Published"
            />
          </If>
          <ul style={{ margin: 0, padding: 0 }}>
            {
              activeShowcases.map(value => (
                <ShowcaseIndexCard
                  key={`nonad-showcase-index-card-${value.showcaseId}`}
                  showcase={value}
                  serviceProviderId={serviceProviderId}
                  archiveShowcase={archiveShowcase}
                  isLoading={showcasePendingRemoval === value.showcaseId}
                  isDraggable={false}
                />
              ))
            }
          </ul>
          <ul style={{ margin: 0, padding: 0 }}>
            {
              inactiveShowcases.map(value => (
                <ShowcaseIndexCard
                  key={`nonad-showcase-index-card-${value.showcaseId}`}
                  showcase={value}
                  serviceProviderId={serviceProviderId}
                  archiveShowcase={archiveShowcase}
                  isLoading={showcasePendingRemoval === value.showcaseId}
                  isDraggable={false}
                  labelText="Inactive"
                  showDisplayButton
                  onDisplayButtonClick={openDisplayModal}
                />
              ))
            }
          </ul>
          <div className="clear-both" />
          <p className="photos-cta-margins">
            Want to upload a single photo or a team headshot? Head to
            <a
              id="Edit-Profile--MenuNav-Photo-Gallery"
              href={urlCatalog.office.spProfile.images(serviceProviderId)}
              style={{ color: '#02819E', fontWeight: 'bold' }}
            >
              &nbsp;photos.
            </a>
          </p>
        </div>
      </Row>
    </div>
  );
};

export default React.memo(ShowcaseNonAdPublishedTab);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/ShowcaseNonAdPublishedTab.js